import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaMapMarkerAlt,
  FaBoxOpen,
  FaLock,
  FaSignOutAlt,
  FaTrashAlt,
  FaHeart,
  FaUndoAlt,
} from "react-icons/fa";
import { getCsrfToken } from "../services/authService";
import { useAuth } from "../hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Profile.css";

export default function Profile() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initial =
    user?.name?.charAt(0).toUpperCase() ||
    user?.email?.charAt(0).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(t("profile.deleteAccountConfirm"));

    if (!confirmed) {
      return;
    }

    try {
      const csrf = await getCsrfToken();

      const response = await fetch("/api/profile/me", {
        method: "DELETE",
        credentials: "include",
        headers: {
          [csrf.headerName]: csrf.token,
        },
      });

      if (response.ok) {
        await logout();
        navigate("/", { replace: true });
      }
    } catch {
      alert(t("profile.deleteAccountError"));
    }
  };

  const location = useLocation();

  const [showSuccess, setShowSuccess] = useState(
    !!location.state?.profileUpdated
  );

  const [showPasswordSuccess, setShowPasswordSuccess] = useState(
    !!location.state?.passwordChanged
  );

  useEffect(() => {
    if (location.state?.profileUpdated) {
      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, []);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  return (
    <main className="profile-page">
      <section className="profile-dashboard">
        {showSuccess && (
          <div className="profile-success">
            {t("profile.updatedSuccessfully")}
          </div>
        )}
        {showPasswordSuccess && (
          <div className="profile-success">
            {t("profile.passwordChangedSuccessfully")}
          </div>
        )}
        <div className="profile-hero">
          <div className="profile-avatar">{initial}</div>

          <div>
            <h1>{t("profile.title")}</h1>
            <p>{user?.name} · {user?.email}</p>
          </div>
        </div>

        <div className="profile-grid">
          <article className="profile-tile">
            <FaUser className="profile-tile-icon" />
            <h2>{t("profile.personalData")}</h2>
            <p>{t("profile.personalDataText")}</p>

            <div className="profile-mini-info">
              <span>{t("profile.name")}: {user?.name || "-"}</span>
              <span>{t("profile.lastname1")}: {user?.lastname1 || "-"}</span>
              <span>{t("profile.email")}: {user?.email || "-"}</span>
            </div>

            <button onClick={() => navigate("/profile/edit")}>
              {t("profile.editData")}
            </button>
          </article>

          <article className="profile-tile">
            <FaHeart className="profile-tile-icon" />
            <h2>{t("favorites.title")}</h2>
            <p>{t("favorites.profileText")}</p>

            <button onClick={() => navigate("/favorites")}>
              {t("favorites.viewFavorites")}
            </button>
          </article>

          <article className="profile-tile">
            <FaMapMarkerAlt className="profile-tile-icon" />
            <h2>{t("profile.addresses")}</h2>
            <p>{t("profile.addressesText")}</p>
            <button onClick={() => navigate("/addresses")}>
              {t("profile.manageAddresses")}
            </button>
          </article>

          <article className="profile-tile">
            <FaBoxOpen className="profile-tile-icon" />
            <h2>{t("profile.orders")}</h2>
            <p>{t("profile.ordersText")}</p>
            <button onClick={() => navigate("/orders")}>
              {t("profile.viewOrders")}
            </button>
          </article>

          <article className="profile-tile">
            <FaUndoAlt className="profile-tile-icon" />
            <h2>{t("profile.returns")}</h2>
            <p>{t("profile.returnsText")}</p>

            <button onClick={() => navigate("/returns")}>
              {t("profile.viewReturns")}
            </button>
          </article>

          <article className="profile-tile">
            <FaLock className="profile-tile-icon" />
            <h2>{t("profile.security")}</h2>
            <p>{t("profile.securityText")}</p>
            <button onClick={() => navigate("/change-password")}>
              {t("profile.changePassword")}
            </button>
          </article>
        </div>

        <button className="profile-logout" onClick={handleLogout}>
          <FaSignOutAlt />
          {t("profile.logout")}
        </button>

        <div className="profile-danger-zone">
          <h2>{t("profile.dangerZone")}</h2>
          <p>{t("profile.deleteAccountText")}</p>

          <button className="profile-delete-account" onClick={handleDeleteAccount}>
            <FaTrashAlt />
            {t("profile.deleteAccount")}
          </button>
        </div>
      </section>
    </main>
  );
}